import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, tap } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { ReportsService } from '@services/reports.service';
import {
  deleteReportTemplateItem,
  loadReportTemplatesList,
  loadReportTemplatesListFailure,
  loadReportTemplatesListSuccess,
  updateReportTemplateItem,
} from '@store/reports/reports.actions';

@Injectable()
export class ReportsEffects {
  private readonly actions$ = inject(Actions);
  private readonly apiService = inject(ReportsService);

  loadReportTemplatesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReportTemplatesList),
      mergeMap(() =>
        this.apiService.listReport().pipe(
          map((list) => loadReportTemplatesListSuccess({ list: list })),
          catchError((error) => of(loadReportTemplatesListFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  updateSelectedTemplateOnListLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadReportTemplatesListSuccess),
        tap(({ list }) => {
          const selected = this.apiService.formGroup.get('selectedTemplateControl')?.value;
          if (!selected) return;

          const exists = list.some((item) => item.id === selected.id);
          if (!exists) {
            this.apiService.formGroup.get('selectedTemplateControl')?.reset();
          } else {
            const updated = list.find((item) => item.id === selected.id);
            const trackSettings = this.apiService.formGroup.get('trackSetting')?.value;
            this.apiService.formGroup.get('selectedTemplateControl')?.setValue(updated);
            this.apiService.formGroup.get('trackSetting')?.setValue(trackSettings);
          }
        }),
      ),
    { dispatch: false },
  );

  updateSelectedTemplateOnItemUpdate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateReportTemplateItem),
        tap(({ id, changes }) => {
          const selected = this.apiService.formGroup.get('selectedTemplateControl')?.value;
          if (selected?.id === id) {
            this.apiService.formGroup
              .get('selectedTemplateControl')
              ?.patchValue({ ...selected, ...changes });
          }
        }),
      ),
    { dispatch: false },
  );

  clearSelectedTemplateOnDelete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteReportTemplateItem),
        tap(({ id }) => {
          const selected = this.apiService.formGroup.get('selectedTemplateControl')?.value;

          if (selected && selected.id === id) {
            this.apiService.formGroup.get('selectedTemplateControl')?.reset();
          }
        }),
      ),
    { dispatch: false },
  );
}
